/**
 * W1 ANALYST persona synthesis (design W1; Agent Brains 13). Descriptive
 * groups of CLOSED threads, by k-modes over codes. Pure and deterministic: no
 * random start, no clock, no model.
 *
 * WHAT A PERSONA IS NOT. It is a description of who bought or left, for the
 * owner to read. It never feeds a price, a quote or a discount: CLAUDE.md
 * section 3 forbids any number differentiated by inferred wealth, and section
 * 0.5 keeps the service level and the figures apart. The `monetary` code is a
 * bucket of what a closed thread paid, an outcome, not a prediction.
 *
 * HALTS LOUDLY. Below `minThreads` labelled closed threads, or when any
 * cluster falls under `minCluster`, the result is `halted` with the n and the
 * bar, and the caller writes `analyst.persona_halted`. Fewer threads than that
 * make a cluster a story rather than a finding (precision beats recall).
 *
 * No model takes part, not even to name a cluster: a cluster is shown by its
 * id, its size and its mode codes (agents/analystWeekly.ts stores them as one
 * `analyst.personas` row per week; /reporting renders them).
 */

export const PERSONA_DIMS = ["inquiryType", "priceBand", "entrySource", "outcome", "recency", "frequency", "monetary"] as const;
export type PersonaDim = (typeof PERSONA_DIMS)[number];

export interface PersonaRow {
  readonly threadId: string;
  readonly codes: Readonly<Record<PersonaDim, string>>;
}

export interface PersonaBars {
  /** Labelled closed threads needed before any synthesis. */
  readonly minThreads: number;
  /** The smallest cluster that is allowed to stand. */
  readonly minCluster: number;
  /** Clusters asked for. */
  readonly k: number;
  readonly maxIter: number;
}

/**
 * Open question 11 of the W design. AWAITING RATIFICATION (Kabir): 100 threads
 * and 20 per cluster are the design's recs; `k` and `maxIter` are builder
 * placeholders (AWAITING RATIFICATION).
 */
export const PERSONA_BARS: PersonaBars = Object.freeze({ minThreads: 100, minCluster: 20, k: 4, maxIter: 20 });

export interface PersonaCluster {
  /** `persona_1` upward, largest first. */
  readonly id: string;
  readonly size: number;
  readonly mode: Readonly<Record<PersonaDim, string>>;
}

export type PersonaSynthesis =
  | {
      readonly status: "halted";
      /** Threads supplied, or the smallest cluster's size when `reason` is `cluster_below_bar`. */
      readonly n: number;
      readonly bar: number;
      readonly reason: "below_thread_bar" | "cluster_below_bar";
      readonly smallestCluster: number | null;
    }
  | { readonly status: "ok"; readonly clusters: readonly PersonaCluster[] };

type Codes = Readonly<Record<PersonaDim, string>>;

function distance(a: Codes, b: Codes): number {
  let d = 0;
  for (const dim of PERSONA_DIMS) if (a[dim] !== b[dim]) d += 1;
  return d;
}

function modeOf(members: readonly PersonaRow[]): Codes {
  const out = {} as Record<PersonaDim, string>;
  for (const dim of PERSONA_DIMS) {
    const tally = new Map<string, number>();
    for (const m of members) tally.set(m.codes[dim], (tally.get(m.codes[dim]) ?? 0) + 1);
    // Most common, ties to the smallest string: never an iteration-order accident.
    out[dim] = [...tally.entries()].sort((x, y) => y[1] - x[1] || x[0].localeCompare(y[0]))[0]?.[0] ?? "";
  }
  return out;
}

export function synthesizePersonas(rows: readonly PersonaRow[], bars: PersonaBars): PersonaSynthesis {
  const first0 = rows[0];
  if (first0 === undefined || rows.length < bars.minThreads) {
    return { status: "halted", n: rows.length, bar: bars.minThreads, reason: "below_thread_bar", smallestCluster: null };
  }
  // Sorted by thread id so the input order never matters.
  const sorted = [...rows].sort((a, b) => a.threadId.localeCompare(b.threadId));
  const k = Math.max(1, Math.min(bars.k, sorted.length));

  // Farthest-first start: the first row, then the row farthest from every start so far.
  const centers: Codes[] = [sorted[0]?.codes ?? first0.codes];
  while (centers.length < k) {
    let best: PersonaRow | undefined;
    let bestD = 0;
    for (const r of sorted) {
      const d = Math.min(...centers.map((c) => distance(c, r.codes)));
      if (d > bestD) {
        bestD = d;
        best = r;
      }
    }
    if (best === undefined) break;
    centers.push(best.codes);
  }

  let assignment: number[] = sorted.map(() => 0);
  for (let iter = 0; iter < bars.maxIter; iter++) {
    const next = sorted.map((r) => {
      let bi = 0;
      let bd = Infinity;
      centers.forEach((c, i) => {
        const d = distance(c, r.codes);
        if (d < bd) {
          bd = d;
          bi = i;
        }
      });
      return bi;
    });
    const changed = next.some((v, i) => v !== assignment[i]);
    assignment = next;
    centers.forEach((_, ci) => {
      const members = sorted.filter((_r, i) => assignment[i] === ci);
      if (members.length > 0) centers[ci] = modeOf(members);
    });
    if (!changed && iter > 0) break;
  }

  const groups = centers.map((_, ci) => sorted.filter((_r, i) => assignment[i] === ci)).filter((g) => g.length > 0);
  const smallest = Math.min(...groups.map((g) => g.length));
  if (smallest < bars.minCluster) {
    return { status: "halted", n: smallest, bar: bars.minCluster, reason: "cluster_below_bar", smallestCluster: smallest };
  }
  const key = (c: Codes): string => PERSONA_DIMS.map((d) => c[d]).join("|");
  const clusters = groups
    .map((g) => ({ size: g.length, mode: modeOf(g) }))
    .sort((a, b) => b.size - a.size || key(a.mode).localeCompare(key(b.mode)))
    .map((c, i) => ({ id: `persona_${i + 1}`, size: c.size, mode: c.mode }));
  return { status: "ok", clusters };
}
